/**
 * Buy Score weight components sub-region inspect view per scope §6.4.
 * Atlas Day 2.5 P7 (adjusted scope per Sol D-1 lock 2026-05-27
 * `docs/atlas-day-2.5-sol-locks.md`).
 *
 * Flat region at (substrate, calculation) cell. Reads
 * FIELD_CLASSIFICATIONS.buy_score_weight_components (7 fields per Phase 0
 * #11 substrate verification; bswc.weight EXCLUDED per D1 (α) lock —
 * weights live in buying_sessions.ui_state.weights per-session scenario
 * state, not engine substrate).
 *
 * D1 (α) catalog-only framing: surface metric catalog (label, key,
 * is_available, applies_to_metric, sort_order, tooltip_text, penalty);
 * NO sum-to-100 phrasing; NO weight values rendered.
 *
 * F33 trap awareness per scope §6.4 + §6.6.5: inline warning when any
 * bswc row has is_available=true AND applies_to_metric IS NULL (engine
 * silently zeros contribution). Component queries bswc directly to check
 * trap condition; renders warning when trap detected; suppresses at 0
 * trap count (Atlas Day 2 Phase 0 #11 verified 0 trap rows today).
 *
 * Consumes resolveTooltip from bswcTooltipFallback per scope §6.4 tooltip
 * surfacing pattern. D-1 lock 2026-05-27: WeightComponentsInspect is the
 * 2nd consumer of resolveTooltip (BuyingSession 1st); Template 18
 * promotion waits for genuine 3rd consumer per BACKLOG L70. NO Template 18
 * promotion this build day.
 */

import { useEffect, useState, type JSX } from 'react';
import { FIELD_CLASSIFICATIONS } from '../../../lib/atlas/classificationMaps';
import { WEIGHT_COMPONENTS_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import { resolveTooltip } from '../../../../../../src/lib/bswcTooltipFallback';
import type { BuyScoreWeightComponent } from '../../../../../../src/types';
import { supabase } from '../../../lib/supabase';
import { AlertTriangle } from 'lucide-react';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function WeightComponentsInspect(): JSX.Element {
  const bswcFields = FIELD_CLASSIFICATIONS.buy_score_weight_components;
  const fieldEntries = Object.entries(bswcFields);

  const strategicFields = fieldEntries.filter(([, c]) => c.layer5Nature === 'strategic');
  const mechanicalFields = fieldEntries.filter(([, c]) => c.layer5Nature === 'mechanical');

  // F33 trap detection — query bswc directly for active rows + tooltip surfacing
  const [bswcRows, setBswcRows] = useState<BuyScoreWeightComponent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('buy_score_weight_components')
      .select('id, key, label, penalty, is_available, sort_order, applies_to_metric, tooltip_text')
      .order('sort_order', { ascending: true })
      .then((res) => {
        if (cancelled) return;
        setBswcRows((res.data ?? []) as unknown as BuyScoreWeightComponent[]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeRows = bswcRows.filter((r) => r.is_available);
  const f33TrapRows = activeRows.filter((r) => r.applies_to_metric == null);

  return (
    <InspectShell
      subRegionName="Weight components"
      surfaceLine="ISG defines N candidate metrics for ranking; K active. Per-session weighting in buying sessions. → composed into Buy Score above"
    >
      {/* F33 trap inline warning per scope §6.4 + §6.6.5 — conditional on substrate state */}
      {!loading && f33TrapRows.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-500">
            <strong>Configuration trap:</strong> {f33TrapRows.length}{' '}
            {f33TrapRows.length === 1 ? 'weight component' : 'weight components'} active but not
            mapped to a metric ({f33TrapRows.map((r) => r.key).join(', ')}). Engine silently
            zeros the contribution per Day 91a F33 framing.
          </p>
        </div>
      )}

      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per scope §6.4: per-row split. Weight values = <strong>judgment</strong> (ranking
          weight choices — but weights live per-session per D1 (α) lock, not engine substrate).{' '}
          <code>is_available</code> = <strong>judgment</strong> (which metrics participate);{' '}
          <code>penalty</code> = <strong>judgment</strong>; <code>sort_order</code> ={' '}
          <strong>mechanical</strong> (display order); <code>applies_to_metric</code> ={' '}
          <strong>mechanical</strong> (structural mapping); <code>tooltip_text</code> =
          ownership-altitude documentation choice.
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <p>Mostly strategic per scope §6.4. Per-field:</p>
        <div className="mt-2">
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Strategic section ({strategicFields.length} fields)
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {strategicFields.map(([field, c]) => (
              <li key={field}>
                <code>{field}</code>
                {c.rationale ? ` — ${c.rationale.split(';')[0]}` : ''}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-3">
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Mechanical section ({mechanicalFields.length} fields)
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {mechanicalFields.map(([field, c]) => (
              <li key={field}>
                <code>{field}</code>
                {c.rationale ? ` — ${c.rationale.split(';')[0]}` : ''}
              </li>
            ))}
          </ul>
        </div>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <p>Per scope §6.4 L6 + classification map:</p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {fieldEntries.map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> —{' '}
              <span className="font-mono">{c.layer6Authority ?? 'N/A'}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Per scope §6.4 L7: <strong>fully-traced</strong> for tracked fields per{' '}
          <code>bswc.provenance</code> sidecar (Day 67 P2.2 substrate + Day 82 P6.1 triggers
          cover bswc). Per-row trace per change history available.
        </p>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>Curated map (verified as of 2026-05-27 per scope §8 staleness marker):</p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {WEIGHT_COMPONENTS_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Per scope §6.4 L9 + classification map: reversible-with-residue for is_available +
          weight + penalty (toggle propagates to future Buy Score calculations; past
          calculations in <code>product_profitability</code> cache reflect prior state;
          buying-session scenarios may lock to prior state via <code>param_snapshot</code>).
          Per-field:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {fieldEntries.map(([field, c]) => (
            <li key={field}>
              <code>{field}</code> — <span className="font-mono">{c.layer9Reversibility}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Per scope §6.4 L11: surfaces in title block sentence 1 ranking clause (illustrative —
          actual rendered metric list reads from current bswc substrate at render time per
          Day 1 D5 conditional pattern; Atlas Day 1 shipped 7-metric enumeration per active
          bswc labels, not the 4-metric scope-draft example).
        </p>
        {!loading && activeRows.length > 0 && (
          <>
            <p className="mt-2">
              <strong>Tooltips per active metric</strong> (via{' '}
              <code>resolveTooltip</code> from <code>bswcTooltipFallback</code> — 2nd consumer
              per D-1 lock 2026-05-27; Template 18 promotion waits for 3rd consumer per
              BACKLOG L70):
            </p>
            <ul className="list-disc pl-5 text-xs space-y-0.5 mt-1">
              {activeRows.map((row) => (
                <li key={row.id}>
                  <code>{row.key}</code> ({row.label}) — tooltip:{' '}
                  <em>&quot;{resolveTooltip(bswcRows, row.key) ?? '(no tooltip resolved)'}&quot;</em>
                </li>
              ))}
            </ul>
          </>
        )}
      </LayerSection>
    </InspectShell>
  );
}
